import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Warning, Info, ArrowsClockwise } from '@phosphor-icons/react';

const tl = (v) => (v === null || v === undefined ? 'Veri yok' : formatCurrency(v));
const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 }));
const trDate = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Metric = ({ label, value, note, testid }) => (
  <div className="p-3 border rounded-lg" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-xl font-semibold mt-1">{value}</p>
    {note && <p className="text-[11px] text-muted-foreground mt-1">{note}</p>}
  </div>
);

const MiniTable = ({ cols, rows, testid }) => (
  rows.length === 0 ? <p className="text-xs text-muted-foreground" data-testid={testid}>Kayıt yok.</p> : (
    <div className="border rounded-md max-h-72 overflow-auto" data-testid={testid}>
      <Table className="text-xs">
        <TableHeader><TableRow>{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>)}</TableBody>
      </Table>
    </div>
  )
);

const Section = ({ title, source, note, children, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
      {source && <p className="text-xs text-muted-foreground">Kaynak: {source}</p>}
    </CardHeader>
    <CardContent className="space-y-3">
      {children}
      {note && <p className="text-xs text-amber-700 flex gap-1"><Info size={14} className="shrink-0 mt-0.5" />{note}</p>}
    </CardContent>
  </Card>
);

const Alerts = ({ alerts }) => (
  <Section title="Yönetici Uyarı Merkezi" testid="g360-alerts">
    {alerts.length === 0 ? <p className="text-sm">Uyarı yok.</p> : alerts.map((a, i) => (
      <div key={i} className="flex items-start gap-2 text-sm" data-testid={`g360-alert-${i}`}>
        {a.level === 'warning' ? <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" /> : <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />}
        <Badge variant="outline" className="shrink-0">{a.area}</Badge><span>{a.message}</span>
      </div>
    ))}
  </Section>
);

const Revenue = ({ d }) => (
  <Section title="Ciro ve Giderler" note={d.revenue.note} testid="g360-revenue">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Metric testid="g360-erp-revenue" label="ERP günlük ciro toplamı" value={tl(d.revenue.erp.total)}
        note={`${d.revenue.erp.days_with_data}/${d.period.days} gün kayıtlı (%${d.revenue.erp.coverage_pct}); ${d.revenue.erp.missing_days} gün eksik`} />
      <Metric testid="g360-erp-avg" label="ERP kayıtlı gün ortalaması" value={tl(d.revenue.erp.daily_avg)} note="Yalnızca kayıtlı günler" />
      <Metric testid="g360-pos-net" label="POS net satış (rapor yüklenen günler)" value={tl(d.revenue.pos.net_sales)}
        note={`${d.revenue.pos.report_days}/${d.period.days} gün POS raporu (%${d.revenue.pos.coverage_pct})`} />
      <Metric testid="g360-expense-total" label="Gider toplamı" value={tl(d.expenses.total)} note={d.expenses.total === null ? 'Bu dönemde gider kaydı yok (sıfır kabul edilmedi)' : `${d.expenses.record_count} kayıt · ${d.expenses.excluded} olası mükerrer kayıt hariç`} />
    </div>
    <p className="text-xs text-muted-foreground">Gider kaynağı: {d.expenses.source}</p>
    <MiniTable testid="g360-expense-table" cols={['Kategori', 'Kayıt', 'Tutar']} rows={d.expenses.categories.map((c) => [c.label, c.count, tl(c.total)])} />
  </Section>
);

const Purchases = ({ d }) => (
  <Section title="Satın Alma Analizi" source={d.purchases.source} note={d.purchases.note} testid="g360-purchases">
    <MiniTable testid="g360-purchase-table" cols={['Grup', 'Kayıt', 'Miktar (birim bazında)', 'Tutar']}
      rows={d.purchases.groups.map((g) => [g.label, g.count, g.units.map((u) => `${n(u.quantity)} ${u.unit}`).join(' · '), tl(g.total)])} />
  </Section>
);

const Stock = ({ d }) => (
  <Section title="Depo ve Stok Kontrolü" source={d.stock.source} note={d.stock.note} testid="g360-stock">
    <div className="grid lg:grid-cols-3 gap-3">
      {Object.entries(d.stock.locations).map(([k, l]) => (
        <div key={k} className="space-y-2" data-testid={`g360-stock-${k}`}>
          <Metric label={l.label} value={tl(l.estimated_value)}
            note={`${l.products_with_stock} üründe stok · fiyatsız ${l.no_price} · birim belirsiz ${l.unit_unclear}`} />
          <MiniTable cols={['Ürün', 'Miktar', 'Tahmini değer']} rows={l.top.map((r) => [r.name, `${n(r.quantity)} ${r.unit}`, r.value === null ? 'Birim belirsiz / fiyat yok' : tl(r.value)])} />
        </div>
      ))}
    </div>
    <p className="text-sm font-medium pt-2">Stok sayımları</p>
    <p className="text-xs text-muted-foreground">{d.counts.note}</p>
    <MiniTable testid="g360-count-sessions" cols={['Tarih', 'Lokasyon', 'Kalem', 'Farklı kalem', 'Uygulandı']}
      rows={d.counts.sessions.map((s) => [s.date, s.location, s.items, s.items_with_difference, s.applied ? 'Evet' : 'Hayır'])} />
    <MiniTable testid="g360-count-review" cols={['Tarih', 'Lokasyon', 'Ürün', 'Sistem', 'Sayılan', 'Fark', 'Not']}
      rows={d.counts.review.map((r) => [r.date, r.location, r.product, `${n(r.system)} ${r.unit}`, `${n(r.counted)} ${r.unit}`, n(r.difference), r.unit_note || ''])} />
  </Section>
);

const WasteTransfers = ({ d }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <Section title="Zayi / Fire" source={d.waste.source} note={d.waste.note} testid="g360-waste">
      <Metric testid="g360-waste-total" label="Aktif zayi maliyeti (kayıtlı)" value={tl(d.waste.total_cost)} note={`${d.waste.count} kayıt · ${d.waste.cancelled_excluded} iptal hariç`} />
      <MiniTable cols={['Tarih', 'Ürün', 'Miktar', 'Lokasyon', 'Neden', 'Maliyet']} rows={d.waste.rows.map((r) => [r.date, r.product, `${n(r.quantity)} ${r.unit || ''}`, r.location, r.reason, tl(r.cost)])} />
      <p className="text-sm font-medium">Et parçalama firesi (ayrı)</p>
      <MiniTable testid="g360-processing-fire" cols={['Tarih', 'Ürün', 'Fire', 'Maliyet', 'Kaynak']} rows={d.waste.processing_fire.map((r) => [r.date, r.product, `${n(r.quantity)} ${r.unit}`, r.cost === null ? 'Kayıtlı değil' : tl(r.cost), r.source])} />
    </Section>
    <Section title="Tamamlanmış Sevkiyatlar" source={d.transfers.source} note={d.transfers.note} testid="g360-transfers">
      <div className="grid grid-cols-2 gap-3">
        <Metric testid="g360-transfer-count" label="Tamamlanmış sevkiyat" value={d.transfers.completed} note={`${d.transfers.items} kalem`} />
        <Metric testid="g360-transfer-cost" label="Kayıtlı maliyet" value={tl(d.transfers.known_cost_total)} note={`${d.transfers.items_with_cost}/${d.transfers.items} kalemde maliyet var`} />
      </div>
      <p className="text-xs text-muted-foreground">Durumlar: {Object.entries(d.transfers.by_status).map(([k, v]) => `${k} ${v}`).join(', ') || '—'} · tarihsiz tamamlanmış: {d.transfers.completed_undated} (dahil edilmedi)</p>
      <p className="text-sm font-medium">Şüpheli reçeteler</p>
      <MiniTable testid="g360-suspicious" cols={['Reçete', 'Porsiyon maliyeti', 'Satış fiyatı', 'Neden']} rows={d.suspicious_recipes.map((r) => [r.name, r.cost_per_portion, r.selling_price, r.reason])} />
    </Section>
  </div>
);

export default function Gomsu360() {
  const today = trDate();
  const [start, setStart] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [end, setEnd] = useState(iso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/gomsu360', { params: { start, end }, timeout: 60000 });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Panel yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="space-y-6" data-testid="gomsu360-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">GOMŞU 360°</h1>
            <p className="text-sm text-muted-foreground">Yönetici denetim merkezi – kayıtlı verilerden salt okunur özet. Kâr ve maliyet oranı hesaplanmaz.</p>
          </div>
          <div className="flex items-end gap-2">
            <div><Label className="text-xs">Başlangıç</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="g360-start" /></div>
            <div><Label className="text-xs">Bitiş</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="g360-end" /></div>
            <Button onClick={load} disabled={loading} data-testid="g360-load-btn"><ArrowsClockwise size={16} className="mr-2" />{loading ? 'Yükleniyor...' : 'Göster'}</Button>
          </div>
        </div>
        {error && (
          <div className="p-3 border border-destructive rounded-lg text-destructive text-sm flex items-center gap-3" data-testid="g360-error">
            <span>{error}</span><Button size="sm" variant="outline" onClick={load} data-testid="g360-retry-btn">Yeniden Dene</Button>
          </div>
        )}
        {data && (
          <>
            <p className="text-xs text-muted-foreground" data-testid="g360-period">Dönem: {data.period.start} – {data.period.end} ({data.period.days} gün). Hesaplanmayan göstergeler: {data.excluded_metrics.join(', ')}.</p>
            <Alerts alerts={data.alerts} />
            <Revenue d={data} />
            <Purchases d={data} />
            <Stock d={data} />
            <WasteTransfers d={data} />
          </>
        )}
      </div>
    </Layout>
  );
}
